import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * "Continue with Google" inside the Android app (InGo ADR 0023, the app half;
 * ingo-f1, 5 Oct 2026): Google's account sheet through Credential Manager,
 * IngoGooglePlugin.java. Google refuses its web sign-in inside an app's
 * WebView, so on the phone this is the only way to a Google ID token.
 *
 * The token is addressed to the web client id Settings serves, the one the
 * API's token check already accepts. Google must also know the build: an
 * Android OAuth client per signing certificate (Play's app-signing key, the
 * upload key) in ingo-92d5f. A build it does not know ends the sheet as a
 * cancellation ("[16] Account reauth failed.", Jambo), so only a cancellation
 * the person made counts as one; any other is a failure, said and logged.
 *
 * Logged as one serialised line: logcat shows an object as [object Object].
 * The token never is.
 */

const LOG = '[google]';

const IngoGoogle = registerPlugin('IngoGoogle');

/** Credential Manager's words when the person closed the sheet themselves ("activity is cancelled by the user."). */
const BY_THE_PERSON = /cancel+ed by (the )?user/i;

/** Whether Google's sheet can open here: the phone app, a build with the plugin, and Google Play services. */
export async function nativeGoogleAvailable({ capacitor = Capacitor, plugin = IngoGoogle } = {}) {
  try {
    if (!capacitor.isNativePlatform() || !capacitor.isPluginAvailable('IngoGoogle')) return false;
    const answer = await plugin.isAvailable();

    return answer?.available === true;
  } catch (e) {
    console.warn(`${LOG} could not ask whether Google can open here: ${e?.message || e}`);

    return false;
  }
}

/**
 * Google's sheet for the web client id. `{ kind: 'token', idToken }`;
 * `{ kind: 'cancelled' }` when the person closed it; `{ kind: 'failed', reason }`
 * for anything else, Google's disguised refusals included. Never rejects.
 */
export async function nativeGoogleToken(serverClientId, { plugin = IngoGoogle } = {}) {
  let answer;
  try {
    answer = await plugin.signIn({ serverClientId });
  } catch (e) {
    answer = { kind: 'failed', reason: e?.message || String(e) };
  }

  if (answer?.kind === 'token' && typeof answer.idToken === 'string' && answer.idToken !== '') {
    return { kind: 'token', idToken: answer.idToken };
  }
  if (answer?.kind === 'cancelled' && BY_THE_PERSON.test(answer.reason ?? '')) {
    console.info(`${LOG} the sheet was closed by the person`);

    return { kind: 'cancelled' };
  }

  const reason = answer?.kind === 'cancelled' ? `cancelled, not by the person: ${answer.reason ?? ''}` : answer?.reason || 'no token';
  console.warn(`${LOG} Google sign-in did not complete on the phone: ${JSON.stringify({ reason })}`);

  return { kind: 'failed', reason };
}

export const nativeGoogle = { available: nativeGoogleAvailable, token: nativeGoogleToken };
